import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { EmergencyBar } from "@/components/site/EmergencyBar";
import { StickyCallBar } from "@/components/site/StickyCallBar";
import { plumberSchema } from "@/components/site/SchemaJsonLd";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: ({ matches }) => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "author", content: "Phoenix Plumbing Co." },
      { name: "theme-color", content: "#D62828" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Phoenix Plumbing Co." },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@PhxPlumbingCo" },
      { title: "Phoenix Plumber | Emergency Plumbing Service | Phoenix Plumbing Co." },
      { name: "description", content: "Need a Phoenix plumber? Phoenix Plumbing Co. helps with water heaters, drains, leaks, sewer lines, fixtures, gas lines, and emergency plumbing service. Call 602-834-1208." },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/CxLTeNrR61Zxjyjl7S5kdYUlpsI2/social-images/social-1780026853102-PhoenixPlumbingLogoSmall.webp" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/CxLTeNrR61Zxjyjl7S5kdYUlpsI2/social-images/social-1780026853102-PhoenixPlumbingLogoSmall.webp" },
      { name: "google-site-verification", content: "Kv8bJpVG1Gr-3wxOEzHrLogw7wXMEzmbdSR8fp9sVOw" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.ico" },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/inter-latin.woff2",
        crossOrigin: "anonymous",
      },
      { rel: "preconnect", href: "https://www.googletagmanager.com" },
      { rel: "dns-prefetch", href: "https://www.google-analytics.com" },
    ],

    scripts: [
      // Homepage emits its own @graph (WebSite + business) instead.
      ...(matches.some((m) => (m.routeId as string) === "/")
        ? []
        : [{ type: "application/ld+json", children: JSON.stringify(plumberSchema()) }]),
      {
        async: true,
        src: "https://news.google.com/swg/js/v1/publisher.js",
      },

      {
        async: true,
        src: "https://www.googletagmanager.com/gtag/js?id=G-56M8087DPZ",
      },
      {
        children: `window.dataLayer = window.dataLayer || [];\nfunction gtag(){dataLayer.push(arguments);}\ngtag('js', new Date());\ngtag('config', 'G-56M8087DPZ');`,
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:bg-primary focus:text-primary-foreground focus:px-3 focus:py-2 focus:rounded">
        Skip to content
      </a>
      <EmergencyBar />
      <Header />
      <main id="main" className="min-h-[60vh]">
        <Outlet />
      </main>
      <Footer />
      <StickyCallBar />
      <div className="h-14 md:hidden" aria-hidden />
    </QueryClientProvider>
  );
}
